import { GoogleGenAI } from "@google/genai";
import { ChipDNA } from '../types.ts';
import { storage } from '../lib/firebase.ts';
import { ref, getBlob } from 'firebase/storage';

const getClient = () => {
  return new GoogleGenAI({ apiKey: process.env.API_KEY || '' });
};

const ensureApiKey = async () => {
  const win = window as any;
  if (win.aistudio && win.aistudio.hasSelectedApiKey) {
     const hasKey = await win.aistudio.hasSelectedApiKey();
     if (!hasKey) {
       throw new Error("API_KEY_MISSING");
     }
  }
};

const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') resolve(reader.result);
      else reject(new Error("Failed to read blob"));
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const getStoragePathFromUrl = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    if (urlObj.hostname.includes('firebasestorage.googleapis.com')) {
      const parts = urlObj.pathname.split('/o/');
      if (parts.length > 1) {
        return decodeURIComponent(parts[1]);
      }
    }
  } catch (e) {
    return null;
  }
  return null;
};

const loadViaFirebaseSDK = async (url: string): Promise<string> => {
  if (!storage) throw new Error("Storage not initialized");
  let fileRef;
  try {
    if (url.includes('firebasestorage.googleapis.com')) fileRef = ref(storage, url);
  } catch (e) {}

  if (!fileRef) {
    const path = getStoragePathFromUrl(url);
    if (path) fileRef = ref(storage, path);
  }

  if (!fileRef) throw new Error("Could not create Firebase Storage reference");
  
  const blob = await getBlob(fileRef, 50 * 1024 * 1024);
  return await blobToBase64(blob);
};

export const loadAssetAsBase64 = async (url: string): Promise<string> => {
  if (url.startsWith('data:')) return url;
  if (url.includes('firebasestorage.googleapis.com')) {
    try {
      return await loadViaFirebaseSDK(url);
    } catch (e) {
      console.warn("Firebase SDK load failed:", e);
    }
  }
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
  const blob = await response.blob();
  return await blobToBase64(blob);
};

const constructPrompt = (
  basePrompt: string, 
  dna: ChipDNA, 
  environment: string, 
  styleLevel: number, 
  hasReference: boolean, 
  attire: string,
  hasLogo: boolean,
  hasEnvImage: boolean,
  additionalContext: string
) => {
  let prompt = `
    Character: ${dna.core.name} (${dna.core.archetype}).
    Visual Description: ${dna.visual.body} ${dna.visual.face}
    Strict Restrictions: ${dna.visual.restrictions.join(', ')}.
    Personality Vibe: ${dna.core.values.join(', ')}.
    Context: ${dna.core.backstory}
    Task: Generate an image of Chip in the following scenario: ${basePrompt}.
    Environment: ${environment}.
  `;

  if (attire && attire !== 'Standard (None)') {
    prompt += `\nAttire: Character is wearing ${attire}.`;
  }

  if (additionalContext) prompt += `\nAdditional Instructions: ${additionalContext}`;

  prompt += `\nStyle: ${styleLevel > 5 ? 'Expressive' : 'Subtle'}. 3D Render, Matte white.`;
  return prompt;
};

export const generateChipImage = async (
  scenario: string,
  environment: string,
  style: number,
  dna: ChipDNA, 
  highQuality: boolean = false,
  referenceImageBase64: string | null = null,
  attire: string = '',
  aspectRatio: string = '1:1',
  logoImageBase64: string | null = null,
  environmentImageBase64: string | null = null,
  additionalContext: string = ''
): Promise<string[]> => {
  try {
    if (highQuality) await ensureApiKey();
    const ai = getClient();
    const model = highQuality ? 'gemini-3-pro-image-preview' : 'gemini-2.5-flash-image';
    const parts: any[] = [];

    if (referenceImageBase64) {
       const clean = referenceImageBase64.split('base64,')[1] || referenceImageBase64;
       parts.push({ inlineData: { data: clean, mimeType: 'image/jpeg' } });
    }

    if (logoImageBase64) {
      const clean = logoImageBase64.split('base64,')[1] || logoImageBase64;
      parts.push({ inlineData: { data: clean, mimeType: 'image/png' } });
    }

    const prompt = constructPrompt(scenario, dna, environment, style, !!referenceImageBase64, attire, !!logoImageBase64, !!environmentImageBase64, additionalContext);
    parts.push({ text: prompt });

    const response = await ai.models.generateContent({
      model: model,
      contents: { parts },
      config: { imageConfig: { aspectRatio } }
    });

    const images: string[] = [];
    if (response.candidates?.[0]?.content?.parts) {
      for (const part of response.candidates[0].content.parts) {
        if (part.inlineData) images.push(`data:${part.inlineData.mimeType};base64,${part.inlineData.data}`);
      }
    }
    return images.length > 0 ? images : [`https://picsum.photos/1080/1080?random=${Date.now()}`];
  } catch (error: any) {
    if (error.message === "API_KEY_MISSING") throw error;
    return [`https://picsum.photos/1080/1080?random=${Date.now()}`];
  }
};

export const generateChipVideo = async (
  prompt: string,
  referenceImageInput: string | null = null,
  aspectRatio: string = '16:9'
): Promise<string> => {
  await ensureApiKey();
  const ai = getClient();
  let imageParam = undefined;
  if (referenceImageInput) {
    const dataUrl = await loadAssetAsBase64(referenceImageInput);
    const parts = dataUrl.split(',');
    imageParam = { imageBytes: parts[1], mimeType: parts[0].match(/:(.*?);/)?.[1] || 'image/png' };
  }

  let operation = await ai.models.generateVideos({
    model: 'veo-3.1-fast-generate-preview',
    prompt: `Character: Matte white mascot Chip. Action: ${prompt}.`,
    image: imageParam,
    config: { numberOfVideos: 1, resolution: '720p', aspectRatio: aspectRatio as any }
  });

  while (!operation.done) {
    await new Promise(resolve => setTimeout(resolve, 10000));
    operation = await ai.operations.getVideosOperation({operation: operation});
  }

  const uri = operation.response?.generatedVideos?.[0]?.video?.uri;
  return uri ? `${uri}&key=${process.env.API_KEY}` : '';
};

export const generateCaptions = async (
  context: string,
  platform: string,
  tone: string,
  dna: ChipDNA
): Promise<string[]> => {
  const ai = getClient();
  const response = await ai.models.generateContent({
    model: 'gemini-3-flash-preview',
    contents: `Write 5 captions for ${platform} as ${dna.core.name}. Context: ${context}. Tone: ${tone}.`,
    config: { responseMimeType: "application/json" }
  });
  const text = response.text || "[]";
  try {
    const json = JSON.parse(text);
    return Array.isArray(json) ? json : [text];
  } catch (e) {
    return [text];
  }
};